import {
    BottomTabBarProps,
    BottomTabHeaderProps,
    BottomTabNavigationOptions,
    createBottomTabNavigator
} from '@react-navigation/bottom-tabs';
import { Flex, useBreakpointValue } from 'native-base';
import React from 'react';
import { TouchableOpacity } from 'react-native';

import { Color } from '~/components/color';
import { HelttiHeaderBottomTabBarWrapper } from '~/components/heltti-header';
import { LogoutIcon } from '~/components/icon';
import { Spacing } from '~/components/spacing';
import { useIntl } from '~/contexts/intl';
import { useSignOut } from '~/hooks/sign-out';
import { noHeader } from '~/navigator/screen-options';
import { isFunction } from '~/utils';
import { isWeb } from '~/utils/platform';

import {
    TabBarCaptionBadgeContainer,
    TabBarItemBadge,
    TabBarItemCaption,
    TabBarItemContainer,
    TabBarItemFunction,
    TabNavigatorParamList,
    tabNavigationScreens
} from './tab-navigation-config';

const { Navigator, Screen } = createBottomTabNavigator<TabNavigatorParamList>();

const TabBar: React.FC<BottomTabBarProps> = props => {
    const { state, descriptors, navigation, insets } = props;

    const { formatMessage } = useIntl();
    const signOut = useSignOut();

    const showBottomTabBar = useBreakpointValue({
        base: true,
        md: false
    });

    if (!showBottomTabBar) {
        return null;
    }

    return (
        <Flex
            flexDirection="row"
            accessibilityRole={isWeb() ? 'tablist' : undefined}
            bgColor={Color.BACKGROUND_DEFAULT}
            borderTopWidth="2px"
            borderTopColor={Color.LINE_DEFAULT}
            pt={`${Spacing.SMALL}px`}
            pb={`${insets.bottom}px`}
        >
            {state.routes.map((route, index) => {
                const { options } = descriptors[route.key];
                const { tabBarIcon: TabBarIcon, tabBarLabel: TabBarLabel, tabBarBadge } = options;
                const focused = state.index === index;

                const onPress = () => {
                    const event = navigation.emit({
                        type: 'tabPress',
                        target: route.key,
                        canPreventDefault: true
                    });

                    if (!focused && !event.defaultPrevented) {
                        navigation.navigate(route.name);
                    }
                };

                return (
                    <TouchableOpacity
                        key={route.key}
                        accessibilityRole={isWeb() ? 'tab' : 'button'}
                        accessibilityState={{ selected: focused }}
                        aria-selected={isWeb() ? focused : undefined}
                        accessibilityLabel={options.tabBarAccessibilityLabel}
                        testID={options.tabBarTestID}
                        onPress={onPress}
                        style={{ flex: 1, alignItems: 'center' }}
                    >
                        <TabBarItemContainer /* TODO: layout={Layout.duration(300)}*/>
                            {isFunction<TabBarItemFunction>(TabBarIcon) && TabBarIcon({ focused })}
                            <TabBarCaptionBadgeContainer>
                                {isFunction<TabBarItemFunction>(TabBarLabel) && <TabBarLabel focused={focused} />}
                                {tabBarBadge ? <TabBarItemBadge /> : null}
                            </TabBarCaptionBadgeContainer>
                        </TabBarItemContainer>
                    </TouchableOpacity>
                );
            })}
            {isWeb() && (
                <TouchableOpacity
                    key="sign-out"
                    accessibilityRole="button"
                    accessibilityLabel={formatMessage('profile.sign-out')}
                    onPress={signOut}
                    style={{ flex: 1, alignItems: 'center' }}
                >
                    <TabBarItemContainer>
                        <LogoutIcon fill={Color.ICON_PRIMARY} size={32} />
                        <TabBarCaptionBadgeContainer>
                            <TabBarItemCaption
                                maxFontSizeMultiplier={1.5}
                                numberOfLines={1}
                                style={{ color: Color.ICON_DEFAULT }}
                            >
                                {formatMessage('profile.sign-out')}
                            </TabBarItemCaption>
                        </TabBarCaptionBadgeContainer>
                    </TabBarItemContainer>
                </TouchableOpacity>
            )}
        </Flex>
    );
};

export const TabsNavigator = () => {
    const { formatMessage } = useIntl();
    const responsiveHeader: BottomTabNavigationOptions = useBreakpointValue({
        base: {
            ...noHeader
        },
        md: {
            header: (props: BottomTabHeaderProps) => <HelttiHeaderBottomTabBarWrapper {...props} />
        }
    });

    return (
        <Navigator tabBar={props => <TabBar {...props} />} screenOptions={{ ...responsiveHeader }}>
            {tabNavigationScreens.map(({ options, ...rest }) => (
                <Screen
                    {...rest}
                    options={{ title: formatMessage(options.titleKey), ...options }}
                    key={options.titleKey}
                />
            ))}
        </Navigator>
    );
};
